import styled from "@emotion/styled"
import { Dialog, css } from "@mui/material"
import * as api from "api"
import CloseButton from "common/CloseButton"
import { memo } from "react"

import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import Shared from "theme/shared"

const buttonCSS = (p) => css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: center;
  ${p.theme.breakpoints.down("sm")} {
    margin: 0;
  }
`

const Styled = {
  Swiper: styled(Swiper)`
    width: calc(100% - 60px);
    height: calc(100% - 60px);
    z-index: 0;
    margin: 30px;
    .swiper-slide {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `,
  CloseButton: styled(CloseButton)`
    top: 0;
    inset-inline-start: 0;
  `,
  Image: styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
  `,
  ButtonNext: styled(Shared.ButtonNext)`
    ${buttonCSS}
    inset-inline-end:0;
    :after {
      content: "prev";
    }
  `,
  ButtonPrev: styled(Shared.ButtonPrev)`
    ${buttonCSS}
    inset-inline-start:0;
    :after {
      content: "next";
    }
  `,
  SwiperSlide: styled(SwiperSlide)`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  IframeWrap: styled.div`
    max-width: 100%;
    width: 1280px;
    position: relative;
  `,
  IframeContainer: styled.div`
    height: 0;
    width: 100%;
    padding-bottom: calc(100% / (16 / 9));
    position: relative;
    iframe {
      width: 100%;
      height: 100%;
      max-height: 100vh;
      position: absolute;
      top: 0;
      left: 0;
    }
  `,
}

const getYoutubeId = (url) => {
  if (!url) return null
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)

  return match && match[2].length === 11 ? match[2] : null
}

type ServiceCenterGalleryDialogProps = {
  onClose: () => void
  images: api.ImageModel[]
  videos: api.ImageModel[]
  active: number | null
}

const ServiceCenterGalleryDialog = ({
  onClose,
  images,
  videos,
  active,
}: ServiceCenterGalleryDialogProps) => {
  const nextEl = "SCGallery-nextEl"
  const prevEl = "SCGallery-prevEl"
  const plural = [...images, ...videos].length > 1

  return (
    <Dialog
      open={active != null}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "transparent" },
      }}
      fullScreen
    >
      <Styled.CloseButton onClick={onClose} />
      <Styled.Swiper
        modules={[Navigation]}
        initialSlide={active}
        slidesPerView={1}
        grabCursor
        navigation={{ nextEl: `.${nextEl}`, prevEl: `.${prevEl}` }}
      >
        {images.map((item, i) => (
          <SwiperSlide key={i}>
            <Styled.Image src={`${item.imageUrl}?width=1280`} alt={item.alt} />
          </SwiperSlide>
        ))}
        {videos.map((item, i) => {
          const videoId = getYoutubeId(item.imageUrl)
          const videoUrl = videoId ? `//www.youtube.com/embed/${videoId}` : null
          if (!videoUrl) return null
          return (
            <SwiperSlide key={i}>
              {({ isActive }) =>
                isActive &&
                videoUrl && (
                  <Styled.IframeWrap>
                    <Styled.IframeContainer>
                      <iframe src={videoUrl} frameBorder="0" />
                    </Styled.IframeContainer>
                  </Styled.IframeWrap>
                )
              }
            </SwiperSlide>
          )
        })}
      </Styled.Swiper>
      <Styled.ButtonNext visible={plural} className={nextEl} />
      <Styled.ButtonPrev visible={plural} className={prevEl} />
    </Dialog>
  )
}

export default memo(ServiceCenterGalleryDialog)
